/**
  * Vue Application Entry Point - Архитектурно правильная реализация
  * Единая точка входа для всех Vue приложений с четким разделением ответственности
  */
 
 import { createApp } from 'vue';
 import { createPinia } from 'pinia';
 import { i18n } from './composables/useI18n';
 import { logger } from './utils/logger';
 
 // Импортируем все компоненты и composables
 import MessagesApp from './MessagesApp.vue';
 import VueSidebar from './components/VueSidebar.vue';
 import MessageGallery from './components/MessageGallery.vue';
 import { useMessagesStore } from './stores/messages';
 import { useUnifiedWebSocket } from './composables/useUnifiedWebSocket';
 import { useTypingIndicator } from './composables/useTypingIndicator';

 const setupWebSocketBridge = (store: ReturnType<typeof useMessagesStore>) => {
   const w = window as any;
   if (w.__vueWsBridgeInitialized) {
     return;
   }

   const bootstrap = w.wsBootstrap;
   if (!bootstrap || typeof bootstrap.on !== 'function') {
     return;
   }

   w.__vueWsBridgeInitialized = true;

   const extract = (payload: any) => {
     if (payload && typeof payload === 'object' && 'detail' in payload) {
       return (payload as any).detail;
     }
     return payload;
   };

   const typing = useTypingIndicator();

   bootstrap.on('websocket:message', (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;
     // Сбрасываем typing индикатор при получении сообщения от любого контакта
    const fromUserId = msg.from_user_id ?? msg.fromUserId ?? msg.userId ?? msg.user_id;
    const chatId = msg.chat_id ?? msg.chatId ?? msg.contact_id ?? msg.contactId;

    if (fromUserId && chatId) {
      // Сбрасываем typing для этого чата при получении сообщения
      if (store.typing[chatId] && store.typing[chatId][fromUserId]) {
        delete store.typing[chatId][fromUserId];
        if (Object.keys(store.typing[chatId]).length === 0) {
          delete store.typing[chatId];
        }
      }
    }

    (store as any).addMessage(msg);
   });

   bootstrap.on('websocket:typing', (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;
     typing.handleTypingEvent(msg);
   });

   const handlePresenceUpdate = (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;

     const data = msg.data && typeof msg.data === 'object' ? msg.data : msg;
     const userId = data.user_id ?? data.userId ?? data.id;
     const rawStatus = data.isOnline ?? data.online ?? data.is_online ?? data.status;
     if (userId === undefined || userId === null) return;
     const presenceStatus = rawStatus === true || rawStatus === 'online' ? 'online' : rawStatus === 'recently_online' ? 'recently_online' : 'offline';
     const lastSeen = data.last_seen ?? null;
     const lastActivity = data.last_activity ?? null;
     (store as any).setPresence(userId, presenceStatus, lastSeen, lastActivity);
   };

   bootstrap.on('websocket:presence', handlePresenceUpdate);

   const handlePresenceSnapshot = (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;

     const data = msg.data && typeof msg.data === 'object' ? msg.data : msg;
     if (!data.users || typeof data.users !== 'object') return;

     Object.keys(data.users).forEach((userIdKey) => {
       const userId = userIdKey;
       const status = data.users[userIdKey];
       const detail = data.details && data.details[userIdKey] ? data.details[userIdKey] : {};
       const rawStatus = status;
       const presenceStatus = rawStatus === true || rawStatus === 'online' ? 'online' : rawStatus === 'recently_online' ? 'recently_online' : 'offline';
       const lastSeen = detail.last_seen ?? data.last_seen ?? null;
       const lastActivity = detail.last_activity ?? data.last_activity ?? null;
       (store as any).setPresence(userId, presenceStatus, lastSeen, lastActivity);
     });
   };

   bootstrap.on('websocket:presence_snapshot', handlePresenceSnapshot);

   // Обработка события message_read для real-time обновления статусов
   bootstrap.on('websocket:message_read', (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;

     logger.log('Vue WebSocket Bridge: Received message_read event', msg);
     const data = msg.data && typeof msg.data === 'object' ? msg.data : msg;
     (store as any).handleMessageReadEvent(data);
   });

   // Обработка события message_delivered для real-time обновления статуса доставки
   bootstrap.on('websocket:message_delivered', (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;

     logger.log('Vue WebSocket Bridge: Received message_delivered event', msg);
     const data = msg.data && typeof msg.data === 'object' ? msg.data : msg;
     (store as any).handleMessageDeliveredEvent(data);
   });

   // Обработка события message_edited для real-time обновления отредактированных сообщений
   bootstrap.on('websocket:message_edited', (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;

     logger.log('Vue WebSocket Bridge: Received message_edited event', msg);
     const data = msg.data && typeof msg.data === 'object' ? msg.data : msg;
     (store as any).handleMessageEditedEvent(data);
   });

   // Обработка событий навигации (счётчики, реакции, удаление сообщений/диалогов)
  bootstrap.on('websocket:navigation_update', (payload: any) => {
    const msg = extract(payload);
    if (!msg) return;

    const type = msg.type;
    const data = msg.data && typeof msg.data === 'object' ? msg.data : {};
    
    logger.log('Vue WebSocket Bridge: navigation event', { type, data });

    if (type === 'navigation_update' && msg.counters) {
      logger.log('Vue WebSocket Bridge: Updating counters from navigation_update', msg.counters);
      const counterKeys = ['messages', 'notifications', 'likes-from-you', 'likes-to-you', 'likes-mutual', 'guests'];
      counterKeys.forEach((key) => {
        if (window.updateNavigationBadge && msg.counters[key] !== undefined) {
          window.updateNavigationBadge(key, msg.counters[key]);
        }
      });
      return;
    }

    switch (type) {
      case 'message_reaction_changed':
        if (data.message_id && data.reactions && store.currentContactId) {
          // Use updateMessage to create a new message object so v-memo re-renders
          store.updateMessage(data.message_id, {
            chatId: store.currentContactId,
            reactions: data.reactions,
          });
        }
        break;

      case 'messages_deleted':
        if (data.message_ids && Array.isArray(data.message_ids) && store.currentContactId) {
          const chatId = String(store.currentContactId);
          const deletedIds = new Set(data.message_ids.map((id: any) => String(id)));
          store.$patch((state) => {
            if (state.messagesByChat[chatId]) {
              state.messagesByChat[chatId] = state.messagesByChat[chatId].filter(
                (m: any) => !deletedIds.has(String(m.id)) && !deletedIds.has(String(m._serverId))
              );
            }
          });
        }
        break;

      case 'conversation_deleted':
        if (data.contact_id) {
          const contactId = String(data.contact_id);
          const isCurrentConversation = String(store.currentContactId) === contactId;
          
          store.$patch((state) => {
            if (state.conversations[contactId]) {
              delete state.conversations[contactId];
            }
            state.conversationOrder = state.conversationOrder.filter(
              (c: any) => String(c.id) !== contactId && String(c.contact?.id) !== contactId
            );
            
            if (isCurrentConversation) {
              state.currentContactId = null;
            }
          });
          
          if (isCurrentConversation) {
            const messenger = (window as any).messenger;
            if (messenger?.info) {
              messenger.info('Conversation deleted by other user');
            }
          }
        }
        break;
    }
  });

  // Обработка событий уведомлений (включая блокировки)
   bootstrap.on('websocket:notification', (payload: any) => {
     const msg = extract(payload);
     if (!msg) return;

     logger.log('Vue WebSocket Bridge: Received notification event', msg);
     logger.debug('Vue WebSocket Bridge: notification payload/data', { payload: msg });

     const data = msg.data && typeof msg.data === 'object' ? msg.data : msg;
     logger.debug('Vue WebSocket Bridge: notification inner data', data);

     // Обработка уведомлений о блокировках
     if (data.type === 'user_block_changed') {
       logger.debug('Vue WebSocket Bridge: handling block changed event');
       handleBlockChanged(data);
     } else {
       logger.debug('Vue WebSocket Bridge: not a block event, type:', data.type);
     }
   });

   // Функция обработки изменений блокировок
   const handleBlockChanged = (blockData: any) => {
     logger.log('Vue WebSocket Bridge: Block status changed', blockData);

     // Получаем доступ к MessagesApp через глобальное хранилище
     const messagesApp = (window as any).__vueMessagesApp;
     logger.debug('Vue WebSocket Bridge: messagesApp bridge availability', {
       hasApp: !!messagesApp,
       hasHandler: !!(messagesApp && messagesApp.handleBlockChanged),
     });

     if (messagesApp && messagesApp.handleBlockChanged) {
       logger.debug('Vue WebSocket Bridge: delegating to messagesApp.handleBlockChanged');
       messagesApp.handleBlockChanged(blockData);
     } else {
       logger.debug('Vue WebSocket Bridge: using fallback block-handling logic');
       // Fallback - обновляем через store
       const fromUserId = blockData.from_user_id;
       const action = blockData.action;
       
       if (fromUserId && action) {
         // Показываем уведомление пользователю
         const messenger = (window as any).messenger;
         if (messenger) {
           const message = action === 'blocked' 
             ? 'You have been blocked by this user'
             : 'You have been unblocked by this user';
           
           messenger[action === 'unblocked' ? 'success' : 'warning'](message);
         }
         
         // Если это текущий контакт, обновляем статус блокировки
         const currentContactId = store.currentContactId;
         if (currentContactId == fromUserId) {
           // Вызываем проверку статуса блокировки
           if (messagesApp && messagesApp.checkUserBlocked) {
             messagesApp.checkUserBlocked(currentContactId);
           }
         }
       }
     }
   };

   window.addEventListener('messages:sent', (e: Event) => {
     const detail = (e as CustomEvent).detail;
     const contactId = detail && detail.contactId ? String(detail.contactId) : null;
     const contactAvatar: string | null = (detail && detail.contactAvatar) ? String(detail.contactAvatar) : null;
     const contactName: string | null = (detail && detail.contactName) ? String(detail.contactName) : null;
     if (contactId) {
       const chatMessages = (store as any).messagesByChat;
       if (chatMessages && chatMessages[contactId]) {
         delete chatMessages[contactId];
       }
       // Pre-populate contact avatar/name so Vue messages app shows it immediately
       if (contactAvatar || contactName) {
         store.$patch((state: any) => {
           const existing = state.conversations[contactId];
           const contactData = {
             id: contactId,
             avatar: contactAvatar,
             full_name: contactName,
           };
           if (!existing) {
             state.conversations[contactId] = { id: contactId, contact: contactData };
           } else if (!existing.contact?.avatar && contactAvatar) {
             state.conversations[contactId] = {
               ...existing,
               contact: { ...(existing.contact || {}), ...contactData },
             };
           }
         });
       }
     }
     (store as any).loadConversations();
   });
 };

 // Простая реализация vLazy
 const vLazy = {
   mounted(el: HTMLElement, binding: any) {
     if (binding.value) {
       el.style.backgroundImage = `url(${binding.value})`;
     }
   },
   updated(el: HTMLElement, binding: any) {
     if (binding.value) {
       el.style.backgroundImage = `url(${binding.value})`;
     }
   }
 };
 
 /**
  * Создание и управление shared Pinia instance
  */
 class PiniaManager {
   private static instance: any = null;
 
   public static getInstance(): any {
     if (!PiniaManager.instance) {
       PiniaManager.instance = createPinia();
     }
     return PiniaManager.instance;
   }
 
   public static reset(): void {
     PiniaManager.instance = null;
   }
 }
 
 /**
  * Инициализация Vue Messages Application
  */
 const initVueMessages = () => {
   // Предпочитаем standalone-страницу; иначе монтируем модальный root.
   let root: HTMLElement | null = document.getElementById('messages-vue-root-standalone');
   let isEmbedded = false;

   if (!root) {
     root = document.getElementById('messages-vue-root-modal');
     isEmbedded = true;
   }

   // Legacy fallback на случай сторонних override
   if (!root) {
     root = document.getElementById('messages-vue-root');
     if (root) {
       isEmbedded = root.getAttribute('data-embedded') === 'true';
     }
   }

   if (!root) {
     return;
   }
 
   const userIdAttr = root.getAttribute('data-user-id');
   if (userIdAttr) {
     (window as any).__messagesCurrentUserId = userIdAttr;
   }

   const userAvatarAttr = root.getAttribute('data-user-avatar');
   if (userAvatarAttr) {
     (window as any).__messagesCurrentUserAvatar = userAvatarAttr;
   }
   
   const translations = (window as any).__vueMessagesTranslations || {};
   i18n.setTranslations(translations);
 
   const app = createApp(MessagesApp, { embedded: isEmbedded, rootId: root.id });
   const pinia = PiniaManager.getInstance();
   app.use(pinia);
   app.directive('lazy', vLazy);
 
   const store = useMessagesStore();
   (window as any).__vueMessagesStore = store;

   setupWebSocketBridge(store);

   const { connect } = useUnifiedWebSocket();
   connect();

   app.mount(root);
 };
 
 /**
  * Инициализация Vue Sidebar Application
  */
 const initVueSidebar = () => {
   const root = document.getElementById('vue-sidebar-root');
   if (!root) {
     return;
   }
 
   const userIdAttr = root.getAttribute('data-user-id');
   if (userIdAttr) {
     (window as any).__messagesCurrentUserId = userIdAttr;
   }

   const userAvatarAttr = root.getAttribute('data-user-avatar');
   if (userAvatarAttr) {
     (window as any).__messagesCurrentUserAvatar = userAvatarAttr;
   }
   
   const translations = (window as any).__vueMessagesTranslations || {};
   i18n.setTranslations(translations);
 
   const app = createApp(VueSidebar);
   const pinia = PiniaManager.getInstance();
   app.use(pinia);
   app.directive('lazy', vLazy);
 
   const store = useMessagesStore();

   setupWebSocketBridge(store);

   const { connect } = useUnifiedWebSocket();
   connect();

   // Синхронизация с основным Messages store
   if ((window as any).__vueMessagesStore) {
     const mainStore = (window as any).__vueMessagesStore;
     store.conversations = mainStore.conversations;
     store.currentContactId = mainStore.currentContactId;
     store.unread = mainStore.unread;
     store.typing = mainStore.typing;
     store.presence = mainStore.presence;
   }
 
   (window as any).__vueSidebarStore = store;
   (store as any).loadConversations();
 
   app.mount('#vue-sidebar-root');
 };
 
 /**
  * Global offline sync: works on ANY page where Vue loads (sidebar, messages, etc.)
  * This ensures pending offline messages get synced even if user is not on /messages page.
  */
 const setupGlobalOfflineSync = () => {
   const w = window as any;
   if (w.__globalOfflineSyncInitialized) return;
   w.__globalOfflineSyncInitialized = true;

   // When browser comes online, sync pending messages from IndexedDB
   window.addEventListener('online', () => {
     logger.log('Global: Online event detected, syncing pending offline messages');
     try {
       const store = useMessagesStore();
       (store as any).syncPendingMessages();
     } catch (e) {
       console.warn('Global: Failed to sync pending messages on online event:', e);
     }
   });

   // Listen for SW postMessage requesting sync
   if ('serviceWorker' in navigator) {
     navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
       if (event.data && event.data.type === 'sync_pending_messages') {
         logger.log('Global: SW requested sync_pending_messages');
         try {
           const store = useMessagesStore();
           (store as any).syncPendingMessages();
         } catch (e) {
           console.warn('Global: Failed to sync on SW request:', e);
         }
       }
     });
   }

   // Store auth credentials in unifiedDB so SW can use them for background sync
   // when no page is open
   storeAuthForSW();
 };

 /**
  * Store user auth credentials in unifiedDB for SW background sync
  */
 const storeAuthForSW = async () => {
   try {
     const userId = (window as any).__messagesCurrentUserId;
     if (!userId) return;

     const unifiedDB = (window as any).unifiedDB;
     if (!unifiedDB || typeof unifiedDB.set !== 'function') return;

     // Get auth_key from meta tag or global var
     const authKeyMeta = document.querySelector('meta[name="sw-auth-key"]');
     const authKey = authKeyMeta?.getAttribute('content') || (window as any).__swAuthKey || '';

     await unifiedDB.set('user_preferences', {
       key: 'sw_auth',
       userId: String(userId),
       authKey: authKey,
       updatedAt: Date.now(),
     });
     logger.log('Global: Stored auth credentials for SW background sync');
   } catch (e) {
     // Non-critical, ignore
   }
 };

 /**
  * Автоматическая инициализация при загрузке DOM
  */
 const initializeVueApps = () => {
   // Инициализируем оба приложения
   initVueMessages();
   initVueSidebar();

   // Setup global offline sync (works on any page)
   setupGlobalOfflineSync();
 };
 
 /**
  * Запуск автоматической инициализации
  */
 const autoInitialize = () => {
   if (document.readyState === 'loading') {
     document.addEventListener('DOMContentLoaded', initializeVueApps);
   } else {
     initializeVueApps();
   }
 };
 
 // Запускаем автоматическую инициализацию
 autoInitialize();

/**
 * Reusable photo gallery that can be opened from any page (profile, messages, etc.)
 */
const openPhotoGallery = (attachments: any[], index = 0) => {
  const w = window as any;

  if (w.__vuePhotoGalleryApp) {
    w.__vuePhotoGalleryApp.unmount();
    w.__vuePhotoGalleryApp = null;
    w.__vuePhotoGalleryInstance = null;
  }

  const existing = document.getElementById('vue-photo-gallery-mount');
  if (existing) {
    existing.remove();
  }

  const mountEl = document.createElement('div');
  mountEl.id = 'vue-photo-gallery-mount';
  document.body.appendChild(mountEl);

  const translations = w.__vueMessagesTranslations || {};
  i18n.setTranslations(translations);

  const app = createApp(MessageGallery, {
    attachments,
    showThumbnails: false,
  });
  app.use(PiniaManager.getInstance());
  const instance: any = app.mount(mountEl);

  w.__vuePhotoGalleryApp = app;
  w.__vuePhotoGalleryInstance = instance;

  if (instance && typeof instance.openGallery === 'function') {
    instance.openGallery(index);
  }
};

// Expose gallery API globally for non-Vue pages (profile, etc.)
(window as any).openPhotoGallery = openPhotoGallery;

/**
 * Экспорт функций для ручного вызова
 */
export {
  openPhotoGallery,
   initVueMessages,
   initVueSidebar,
   initializeVueApps
 };
 
 /**
  * Default export
  */
 export default {
   initVueMessages,
   initVueSidebar,
   initializeVueApps
 };
